// src/components/messages/AttachmentUploader.js
import React, { useRef, useState } from 'react';
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE_BYTES, MAX_TOTAL_SIZE_BYTES } from '../../services/messagingService';
import MaterialIcon from '../ui/MaterialIcon';

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const AttachmentUploader = ({ files, onChange }) => {
  const inputRef = useRef(null);
  const [errors, setErrors] = useState([]);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const handleAdd = (e) => {
    const selected = Array.from(e.target.files || []);
    const nextErrors = [];
    const valid = [];

    for (const file of selected) {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        nextErrors.push(`${file.name}: file type not allowed`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        nextErrors.push(`${file.name}: exceeds 10 MB limit`);
        continue;
      }
      if (totalSize + file.size + valid.reduce((s, f) => s + f.size, 0) > MAX_TOTAL_SIZE_BYTES) {
        nextErrors.push(`${file.name}: would exceed 25 MB total limit`);
        continue;
      }
      valid.push(file);
    }

    setErrors(nextErrors);
    if (valid.length > 0) onChange([...files, ...valid]);

    // Reset input so same file can be re-added after removal
    e.target.value = '';
  };

  const handleRemove = (index) => {
    setErrors([]);
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="min-h-[48px] flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-amber-300/80 text-primary-dark text-sm font-bold hover:border-secondary hover:text-secondary transition-colors"
        >
          <MaterialIcon name="attach_file" size={18} />
          Attach files
        </button>
        {files.length > 0 && (
          <span className="text-xs text-slate-400">
            {formatBytes(totalSize)} / 25 MB used
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ALLOWED_MIME_TYPES.join(',')}
        className="hidden"
        onChange={handleAdd}
      />

      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((file, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-section-bg rounded-2xl border border-amber-100/40 px-3 py-2">
              <MaterialIcon name="draft" size={18} className="text-secondary flex-shrink-0" />
              <span className="truncate flex-1 min-w-0 font-bold text-primary-dark">{file.name}</span>
              <span className="text-slate-400 text-xs flex-shrink-0">({formatBytes(file.size)})</span>
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="w-9 h-9 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full flex-shrink-0 flex items-center justify-center"
                aria-label={`Remove ${file.name}`}
              >
                <MaterialIcon name="close" size={18} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {errors.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 space-y-1">
          {errors.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-400 flex items-center gap-1">
        <MaterialIcon name="info" size={14} />
        PDF, Word, Excel, PowerPoint, images, text. Max 10 MB per file, 25 MB total.
      </p>
    </div>
  );
};

export default AttachmentUploader;
